import React from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  IconChart,
  IconChevronLeft,
  IconChevronRight,
  IconClipboard,
  IconHome,
  IconLogOut,
  IconMap,
  IconServer,
  IconSettings,
} from './NavIcons';

interface NavigationProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const Navigation: React.FC<NavigationProps> = ({
  currentPage,
  onNavigate,
  collapsed,
  onToggleCollapsed,
}) => {
  const { user, logout, hasRole } = useAuth();

  if (!user) return null;

  const menuItems = [
    { key: 'dashboard', label: 'Dashboard', Icon: IconHome, roles: ['admin', 'manager', 'tenant'] },
    { key: 'reports', label: 'Reports', Icon: IconChart, roles: ['admin', 'manager'] },
    { key: 'requests', label: 'Request Management', Icon: IconClipboard, roles: ['admin', 'manager'] },
    { key: 'zones', label: 'BMS Zones', Icon: IconMap, roles: ['admin'] },
    { key: 'system', label: 'System', Icon: IconServer, roles: ['admin'] },
    { key: 'admin', label: 'Admin', Icon: IconSettings, roles: ['admin'] },
  ];

  const visibleItems = menuItems.filter(item =>
    item.roles.some(role => hasRole(role as 'admin' | 'manager' | 'tenant'))
  );

  return (
    <div className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <span className="sidebar-brand-mark">BEP</span>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <div className="sidebar-brand-title">BEP Dashboard</div>
              <div className="muted sidebar-brand-subtitle">
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)} • {user.username}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      </div>

      <nav className="nav-section sidebar-nav">
        {visibleItems.map(item => {
          const Icon = item.Icon;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onNavigate(item.key)}
              className={`nav-item nav-button ${currentPage === item.key ? 'active' : ''}`}
              title={collapsed ? item.label : undefined}
              aria-label={item.label}
            >
              <div className="nav-row">
                <span className="nav-icon" aria-hidden="true">
                  <Icon />
                </span>
                {!collapsed && <span className="grow nav-label">{item.label}</span>}
              </div>
            </button>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button
          type="button"
          onClick={logout}
          className="nav-item nav-button sidebar-signout"
          title={collapsed ? 'Sign Out' : undefined}
          aria-label="Sign Out"
        >
          <div className="nav-row">
            <span className="nav-icon" aria-hidden="true">
              <IconLogOut />
            </span>
            {!collapsed && <span className="grow nav-label">Sign Out</span>}
          </div>
        </button>
      </div>
    </div>
  );
};

export default Navigation;
